// Странична навигация за широк уеб екран. На телефона я няма — там е
// долната лента. Тук всичко от „Още“ е директно в менюто: уеб системата
// няма нужда от междинен екран, страничната колона побира всичко.

import React, { useEffect, useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { useAuth } from './AuthContext';
import { navigationRef } from './drawer';
import { confirmAction } from './toast';
import { COLORS } from './ui';
import { t } from './i18n';

type Item = { screen: string; label: string; icon: string };
type Group = { title?: string; items: Item[] };

// Функция, за да се преведат етикетите при смяна на езика
function getGroups(): Record<string, Group[]> {
  const GROUPS: Record<string, Group[]> = {
    LANDLORD: [
      {
        items: [
          { screen: 'Dashboard', label: t('Начало'), icon: '🏠' },
          { screen: 'Apartments', label: t('Имоти'), icon: '🏢' },
          { screen: 'Conversations', label: t('Съобщения'), icon: '💬' },
          { screen: 'Issues', label: t('Нередности'), icon: '🛠️' },
        ],
      },
      {
        title: t('Инструменти'),
        items: [
          { screen: 'Listings', label: t('Свободни апартаменти'), icon: '🔎' },
          { screen: 'Documents', label: t('Документи'), icon: '📄' },
          { screen: 'Invoices', label: t('Фактури'), icon: '🧾' },
          { screen: 'Reputation', label: t('Провери наемател'), icon: '🔍' },
          { screen: 'ArchivedApartments', label: t('Архивирани имоти'), icon: '🗄️' },
        ],
      },
      {
        title: t('Акаунт'),
        items: [
          { screen: 'Subscription', label: t('Абонамент'), icon: '⭐' },
          { screen: 'Notifications', label: t('Известия'), icon: '🔔' },
          { screen: 'Profile', label: t('Профил'), icon: '👤' },
        ],
      },
    ],
    TENANT: [
      {
        items: [
          { screen: 'TenantHome', label: t('Моят дом'), icon: '🏠' },
          { screen: 'Issues', label: t('Ремонти'), icon: '🛠️' },
          { screen: 'Conversations', label: t('Съобщения'), icon: '💬' },
        ],
      },
      {
        title: t('Инструменти'),
        items: [
          { screen: 'Listings', label: t('Свободни апартаменти'), icon: '🔎' },
          { screen: 'Documents', label: t('Документи'), icon: '📄' },
          { screen: 'Invoices', label: t('Фактури'), icon: '🧾' },
          { screen: 'Reputation', label: t('Провери наемател'), icon: '🔍' },
        ],
      },
      {
        title: t('Акаунт'),
        items: [
          { screen: 'Notifications', label: t('Известия'), icon: '🔔' },
          { screen: 'Profile', label: t('Профил'), icon: '👤' },
        ],
      },
    ],
    OWNER: [
      {
        items: [
          { screen: 'OwnerApartments', label: t('Имоти'), icon: '🏢' },
          { screen: 'Issues', label: t('Ремонти'), icon: '🛠️' },
          { screen: 'Notifications', label: t('Известия'), icon: '🔔' },
          { screen: 'Profile', label: t('Профил'), icon: '👤' },
        ],
      },
    ],
    HANDYMAN: [
      {
        items: [
          { screen: 'HandymanRequests', label: t('Поръчки'), icon: '🛠️' },
          { screen: 'Notifications', label: t('Известия'), icon: '🔔' },
          { screen: 'Profile', label: t('Профил'), icon: '👤' },
        ],
      },
    ],
    CREATOR: [
      {
        items: [
          { screen: 'CreatorHome', label: t('Начало'), icon: '🏠' },
          { screen: 'CreatorMoney', label: t('Пари'), icon: '💰' },
          { screen: 'CreatorInvites', label: t('Покани'), icon: '💬' },
          { screen: 'Notifications', label: t('Известия'), icon: '🔔' },
          { screen: 'Profile', label: t('Профил'), icon: '👤' },
        ],
      },
    ],
    // Обитател: живее в имота, не е страна по наема — само адрес и нередности
    RESIDENT: [
      {
        items: [
          { screen: 'ResidentHome', label: t('Моят дом'), icon: '🏠' },
          { screen: 'Notifications', label: t('Известия'), icon: '🔔' },
          { screen: 'Profile', label: t('Профил'), icon: '👤' },
        ],
      },
    ],
  };
  GROUPS.AGENCY = [
    {
      items: [
        ...GROUPS.LANDLORD[0].items,
        { screen: 'Owners', label: t('Собственици'), icon: '👥' },
      ],
    },
    GROUPS.LANDLORD[1],
    GROUPS.LANDLORD[2],
  ];
  return GROUPS;
}

// Детайлните екрани светят в раздела, от който са отворени
const OWNED_BY: Record<string, string> = {
  ApartmentDetail: 'Apartments',
  ApartmentStats: 'Apartments',
  AssignOwner: 'Apartments',
  AddApartment: 'Apartments',
  AddTenant: 'Apartments',
  OwnerApartmentDetail: 'OwnerApartments',
  Residents: 'Apartments',
  NewIssue: 'ResidentHome',
  Chat: 'Conversations',
};

function useRouteName(): string | undefined {
  const [name, setName] = useState<string | undefined>(() =>
    navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name : undefined
  );
  useEffect(() => {
    if (!navigationRef.isReady()) return;
    const unsub = navigationRef.addListener('state', () => setName(navigationRef.getCurrentRoute()?.name));
    setName(navigationRef.getCurrentRoute()?.name);
    return unsub;
  }, []);
  return name;
}

const roleLabels = (): Record<string, string> => ({
  ADMIN: t('Админ'),
  MODERATOR: t('Модератор'),
  LANDLORD: t('Наемодател'),
  AGENCY: t('Пропърти мениджър'),
  OWNER: t('Собственик'),
  TENANT: t('Наемател'),
  HANDYMAN: t('Майстор'),
  CREATOR: t('Криейтър'),
  RESIDENT: t('Обитател'),
});

export function WebSidebar() {
  const { user, signOut } = useAuth();
  const route = useRouteName();
  const groups = getGroups()[user?.role ?? ''] ?? [];
  if (!user || groups.length === 0) return null;

  const activeScreen = route && OWNED_BY[route] ? OWNED_BY[route] : route;

  return (
    <View
      style={{
        width: 248,
        backgroundColor: COLORS.card,
        borderRightWidth: 1,
        borderRightColor: COLORS.border,
      }}
    >
      <View style={{ paddingHorizontal: 20, paddingTop: 22, paddingBottom: 14, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Image source={require('../assets/logo-gradient.png')} style={{ width: 30, height: 30 }} resizeMode="contain" />
        <Text style={{ fontSize: 22, fontWeight: '800', color: COLORS.primary }}>Livupp</Text>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 12 }}>
        {groups.map((group, gi) => (
          <View key={gi} style={{ marginTop: gi === 0 ? 0 : 14 }}>
            {group.title && (
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: '700',
                  color: COLORS.muted,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  paddingHorizontal: 20,
                  marginBottom: 4,
                }}
              >
                {group.title}
              </Text>
            )}
            {group.items.map((item) => {
              const active = activeScreen === item.screen;
              return (
                <Pressable
                  key={item.screen}
                  onPress={() => {
                    if (navigationRef.isReady() && route !== item.screen) {
                      navigationRef.navigate(item.screen as never);
                    }
                  }}
                  style={({ hovered }: any) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    paddingVertical: 9,
                    paddingHorizontal: 14,
                    marginHorizontal: 8,
                    borderRadius: 9,
                    backgroundColor: active ? '#E8EEFF' : hovered ? COLORS.bg : 'transparent',
                  })}
                >
                  <Text style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{item.icon}</Text>
                  <Text
                    style={{
                      fontSize: 14.5,
                      fontWeight: active ? '700' : '500',
                      color: active ? COLORS.primary : COLORS.text,
                    }}
                  >
                    {item.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ))}
      </ScrollView>

      <View style={{ borderTopWidth: 1, borderTopColor: COLORS.border, padding: 14 }}>
        <Text style={{ fontWeight: '600', color: COLORS.text }} numberOfLines={1}>
          {user.firstName} {user.lastName}
        </Text>
        <Text style={{ color: COLORS.muted, fontSize: 12.5, marginTop: 1 }}>{roleLabels()[user.role] ?? user.role}</Text>
        <Pressable
          onPress={() => confirmAction(t('Изход'), t('Наистина ли излизате от акаунта?'), t('Изход'), signOut)}
          style={{ marginTop: 8 }}
        >
          <Text style={{ color: COLORS.danger, fontWeight: '600', fontSize: 13.5 }}>{t('Изход от акаунта')}</Text>
        </Pressable>
      </View>
    </View>
  );
}
